import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/seo/SEOHead';
import {
  Shield,
  Users,
  BarChart3,
  FileText,
  Cpu,
  CheckCircle,
  ChevronRight,
  ArrowRight,
  Zap,
  Globe,
  Lock,
  TrendingUp,
} from 'lucide-react';

/* ──────────────────────────────────────────────
   Brand tokens (same as VukzHomePage)
   ────────────────────────────────────────────── */
const V = {
  bg: '#ffffff',
  text: '#111111',
  textSoft: '#555555',
  textMute: '#8a8a8a',
  border: '#e6e6e6',
  cardBg: '#fafafa',
  tagBg: '#f3f3f3',
};

/* ─── Plans ──────────────────────────────────── */
const PLANS = [
  {
    id: 'starter',
    name: 'Governance Starter',
    price: 'US$ 299',
    freq: '/mo',
    desc: 'For small clubs getting started with structured governance, compliance tracking and basic reporting.',
    includes: ['User management (RBAC)', 'Compliance dashboard', 'Basic audit trail', 'E-mail support'],
    highlight: false,
  },
  {
    id: 'pro',
    name: 'Governance Pro',
    price: 'US$ 899',
    freq: '/mo',
    desc: 'For federations and mid-size organizations that need AI-powered insights, advanced reporting and SLA.',
    includes: ['Everything in Starter', 'AI agents (VUKZ Mind)', 'Advanced analytics', 'Financial planning', 'Priority support'],
    highlight: true,
  },
  {
    id: 'enterprise',
    name: 'Governance Enterprise',
    price: 'US$ 2,500+',
    freq: '/mo',
    desc: 'Custom deployment for large sports organizations, private offers and dedicated customer success.',
    includes: ['Everything in Pro', 'Custom integrations', 'Private Azure offer', 'Dedicated CSM', '24/7 critical support'],
    highlight: false,
  },
];

/* ─── What it covers ────────────────────────── */
const COVERS = [
  { icon: Users,     title: 'User & Role Management',     desc: 'Full RBAC with granular permissions per role — Athlete, Coach, Manager, Federation Director.' },
  { icon: FileText,  title: 'Compliance & Audit Trail',   desc: 'Immutable audit logs, LGPD/GDPR controls and Microsoft Purview governance integration.' },
  { icon: BarChart3, title: 'Financial Planning',         desc: 'Club and federation budget tracking, cost allocation and FinOps dashboards.' },
  { icon: Cpu,       title: 'AI Agents',                  desc: 'VUKZ Mind embedded: automated gap detection, quality scores and governance recommendations.' },
  { icon: TrendingUp,title: 'Usage & Billing Analytics',  desc: 'Real-time subscription metrics, consumption dashboards and Azure Marketplace billing integration.' },
  { icon: Lock,      title: 'Data Security & Privacy',    desc: 'Row-Level Security, tenant isolation, encrypted PII and LGPD consent management.' },
];

/* ─── Azure bullets ──────────────────────────── */
const AZURE_BULLETS = [
  'Available via Azure Marketplace — subscribe with your Microsoft account',
  'Powered by Microsoft Azure SQL, Blob Storage and Service Bus',
  'Microsoft Purview for data classification and lineage',
  'LGPD (Brazil) and GDPR (Europe) compliant by design',
  'ISO 27001-aligned architecture with full audit trails',
  'Private offer support for enterprise procurement',
];

/* ──────────────────────────────────────────────────────────────────────────── */
/*  Helper components                                                           */
/* ──────────────────────────────────────────────────────────────────────────── */
const Tag = ({ label }) => (
  <span
    className="text-xs font-medium px-2 py-0.5 rounded"
    style={{ backgroundColor: V.tagBg, color: V.textMute }}
  >
    {label}
  </span>
);

const CoverCard = ({ icon: Icon, title, desc }) => (
  <div
    className="rounded-2xl border p-5 flex flex-col gap-3 hover:shadow-md transition-shadow"
    style={{ backgroundColor: V.cardBg, borderColor: V.border }}
  >
    <div
      className="w-9 h-9 rounded-xl flex items-center justify-center"
      style={{ backgroundColor: '#111', color: '#fff' }}
    >
      <Icon size={18} />
    </div>
    <p className="font-bold text-sm" style={{ color: V.text }}>{title}</p>
    <p className="text-sm leading-relaxed" style={{ color: V.textSoft }}>{desc}</p>
  </div>
);

const PlanCard = ({ name, price, freq, desc, includes, highlight }) => (
  <div
    className="rounded-2xl border flex flex-col overflow-hidden"
    style={{
      borderColor: highlight ? '#111' : V.border,
      borderWidth: highlight ? 2 : 1,
      backgroundColor: V.bg,
    }}
  >
    {highlight && (
      <div className="text-center text-xs font-black py-1.5 tracking-widest" style={{ backgroundColor: '#111', color: '#fff' }}>
        RECOMMENDED
      </div>
    )}
    <div className="p-6 flex flex-col gap-4 flex-1">
      <div>
        <p className="font-black text-base mb-1" style={{ color: V.text }}>{name}</p>
        <p className="text-3xl font-black">{price}<span className="text-sm font-normal" style={{ color: V.textMute }}>{freq}</span></p>
        <p className="text-sm mt-2" style={{ color: V.textSoft }}>{desc}</p>
      </div>
      <ul className="flex flex-col gap-2 flex-1">
        {includes.map(item => (
          <li key={item} className="flex items-start gap-2 text-sm" style={{ color: V.textSoft }}>
            <CheckCircle size={14} style={{ color: '#111', flexShrink: 0, marginTop: 2 }} />
            {item}
          </li>
        ))}
      </ul>
      <Link
        to={name.includes('Enterprise') ? '/contato' : '/marketplace/activate'}
        className="flex items-center justify-center gap-2 w-full font-bold py-3 rounded-xl text-sm mt-2 transition-colors"
        style={highlight
          ? { backgroundColor: '#111', color: '#fff' }
          : { border: `1px solid ${V.border}`, color: V.text }
        }
      >
        {name.includes('Enterprise') ? 'Contact us' : 'Get started'} <ChevronRight size={14} />
      </Link>
    </div>
  </div>
);

/* ──────────────────────────────────────────────────────────────────────────── */
/*  Page                                                                        */
/* ──────────────────────────────────────────────────────────────────────────── */
const EnGovernanceLandingPage = () => (
  <div style={{ backgroundColor: V.bg, color: V.text, fontFamily: 'system-ui, sans-serif' }}>
    <SEOHead
      title="VUKZ Governance Intelligence OS — Sports governance powered by AI"
      description="VUKZ Governance Intelligence OS helps sports organizations manage users, compliance, finances and AI-powered insights — available via Azure Marketplace."
      lang="en"
    />

    {/* ── Nav bar ─────────────────────────────────────────── */}
    <header
      className="sticky top-0 z-50 flex items-center justify-between px-6 py-4 border-b"
      style={{ backgroundColor: V.bg, borderColor: V.border }}
    >
      <Link to="/" className="flex items-center gap-2">
        <img src="/logo-vukz.png" alt="VUKZ" className="h-8 w-8 object-contain" onError={e => { e.target.style.display='none'; }} />
        <span className="text-lg font-black tracking-tight" style={{ color: V.text }}>VUKZ</span>
      </Link>
      <nav className="hidden md:flex items-center gap-6 text-sm" style={{ color: V.textSoft }}>
        <a href="#covers" className="hover:text-black transition-colors">Features</a>
        <a href="#pricing" className="hover:text-black transition-colors">Pricing</a>
        <a href="#azure" className="hover:text-black transition-colors">Azure Marketplace</a>
        <Link to="/en/sports-intelligence" className="hover:text-black transition-colors">Sports OS</Link>
      </nav>
      <div className="flex items-center gap-3">
        <Link to="/login" className="text-sm font-medium" style={{ color: V.textSoft }}>Sign in</Link>
        <Link
          to="/pre-cadastro"
          className="text-sm font-bold px-4 py-2 rounded-xl"
          style={{ backgroundColor: '#111', color: '#fff' }}
        >
          Request access
        </Link>
      </div>
    </header>

    {/* ── Hero ────────────────────────────────────────────── */}
    <section className="max-w-5xl mx-auto px-6 pt-20 pb-16 text-center">
      <div
        className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full border mb-6"
        style={{ borderColor: V.border, color: V.textMute }}
      >
        <Zap size={12} />
        Available on Azure Marketplace
      </div>

      <h1 className="text-5xl font-black leading-tight tracking-tight mb-4">
        Governance Intelligence OS<br />
        <span style={{ color: V.textSoft }}>for sports organizations</span>
      </h1>

      <p className="text-lg max-w-2xl mx-auto mb-10" style={{ color: V.textSoft }}>
        VUKZ Governance Intelligence OS is the{' '}
        <strong>operating system for sports governance</strong> — combining user management,
        compliance, financial planning and AI agents in one integrated platform.
      </p>

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Link
          to="/pre-cadastro"
          className="inline-flex items-center gap-2 font-bold px-6 py-3 rounded-xl"
          style={{ backgroundColor: '#111', color: '#fff' }}
        >
          Request access <ArrowRight size={16} />
        </Link>
        <a
          href="#pricing"
          className="inline-flex items-center gap-2 font-semibold px-6 py-3 rounded-xl border hover:bg-gray-50 transition-colors"
          style={{ borderColor: V.border, color: V.text }}
        >
          See pricing <ChevronRight size={16} />
        </a>
      </div>
    </section>

    {/* ── What it covers ──────────────────────────────────── */}
    <section
      id="covers"
      className="py-16"
      style={{ backgroundColor: V.cardBg, borderTop: `1px solid ${V.border}`, borderBottom: `1px solid ${V.border}` }}
    >
      <div className="max-w-5xl mx-auto px-6">
        <h2 className="text-3xl font-black mb-2">What Governance OS covers</h2>
        <p className="mb-10" style={{ color: V.textSoft }}>
          A complete governance stack — from RBAC to AI-powered audit intelligence.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {COVERS.map(c => <CoverCard key={c.title} {...c} />)}
        </div>
      </div>
    </section>

    {/* ── Pricing ─────────────────────────────────────────── */}
    <section id="pricing" className="max-w-5xl mx-auto px-6 py-16">
      <h2 className="text-3xl font-black mb-2">Pricing</h2>
      <p className="mb-10" style={{ color: V.textSoft }}>
        Start with what you need. Scale as your organization grows.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {PLANS.map(p => <PlanCard key={p.id} {...p} />)}
      </div>
      <p className="text-xs mt-6 text-center" style={{ color: V.textMute }}>
        All prices in USD · Billed monthly via Azure Marketplace ·{' '}
        <Link to="/en/sla" className="underline hover:text-black">SLA</Link> ·{' '}
        <Link to="/en/terms" className="underline hover:text-black">Terms of Service</Link>
      </p>
    </section>

    {/* ── Azure Marketplace ───────────────────────────────── */}
    <section
      id="azure"
      className="py-16"
      style={{ backgroundColor: '#f7f9ff', borderTop: `1px solid ${V.border}` }}
    >
      <div className="max-w-5xl mx-auto px-6 flex flex-col md:flex-row gap-12 items-start">
        <div className="flex-1">
          <div className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: '#0078d4' }}>
            Microsoft Azure
          </div>
          <h2 className="text-3xl font-black mb-4">Secure. Scalable. Marketplace-ready.</h2>
          <p className="mb-8" style={{ color: V.textSoft }}>
            VUKZ Governance Intelligence OS is a SaaS offering on the Azure Marketplace — designed
            for sports organizations that need enterprise-grade governance with zero infrastructure overhead.
          </p>
          <ul className="flex flex-col gap-3">
            {AZURE_BULLETS.map(b => (
              <li key={b} className="flex items-start gap-2 text-sm" style={{ color: V.textSoft }}>
                <CheckCircle size={15} style={{ color: '#0078d4', flexShrink: 0, marginTop: 2 }} />
                {b}
              </li>
            ))}
          </ul>
        </div>
        <div className="flex-1 flex flex-col gap-4">
          <div
            className="rounded-2xl border p-6"
            style={{ backgroundColor: V.bg, borderColor: V.border }}
          >
            <div className="flex items-center gap-2 mb-3">
              <Shield size={18} />
              <p className="font-black text-sm">VUKZ Governance Intelligence OS</p>
            </div>
            <p className="text-xs mb-4" style={{ color: V.textMute }}>
              Offer ID: <code className="font-mono">vukz-governance-os</code>
            </p>
            <Link
              to="/marketplace/activate"
              className="flex items-center justify-center gap-2 w-full font-bold py-3 rounded-xl text-sm"
              style={{ backgroundColor: '#111', color: '#fff' }}
            >
              Activate via Azure Marketplace <ArrowRight size={14} />
            </Link>
          </div>
          <div
            className="rounded-2xl border p-5 text-sm"
            style={{ backgroundColor: V.bg, borderColor: V.border, color: V.textSoft }}
          >
            <p className="font-bold mb-2" style={{ color: V.text }}>Need a private offer?</p>
            <p className="mb-3">Enterprise customers can request a custom private offer through Microsoft Partner Center with custom pricing and SLA terms.</p>
            <Link to="/contato" className="inline-flex items-center gap-1 font-semibold" style={{ color: V.text }}>
              Contact us <ChevronRight size={14} />
            </Link>
          </div>
        </div>
      </div>
    </section>

    {/* ── CTA band ────────────────────────────────────────── */}
    <section className="max-w-5xl mx-auto px-6 py-20 text-center">
      <h2 className="text-4xl font-black mb-4">
        Ready to govern your sports organization with AI?
      </h2>
      <p className="text-lg mb-10 max-w-xl mx-auto" style={{ color: V.textSoft }}>
        VUKZ Governance OS is already managing clubs, federations and performance organizations worldwide.
      </p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Link
          to="/pre-cadastro"
          className="inline-flex items-center gap-2 font-bold px-6 py-3 rounded-xl"
          style={{ backgroundColor: '#111', color: '#fff' }}
        >
          Request access <ArrowRight size={16} />
        </Link>
        <Link
          to="/en/sports-intelligence"
          className="inline-flex items-center gap-2 font-semibold px-6 py-3 rounded-xl border hover:bg-gray-50 transition-colors"
          style={{ borderColor: V.border, color: V.text }}
        >
          Explore Sports Intelligence OS <ChevronRight size={16} />
        </Link>
      </div>
    </section>

    {/* ── Footer ──────────────────────────────────────────── */}
    <footer
      className="border-t px-6 py-10 text-center text-xs"
      style={{ borderColor: V.border, color: V.textMute }}
    >
      <p className="mb-2 font-bold text-sm" style={{ color: V.text }}>VUKZ Governance Intelligence OS</p>
      <p className="mb-4">Arena Stats Ltda. · CNPJ 61.698.906/0001-16 (Brazil)</p>
      <div className="flex justify-center gap-6 flex-wrap">
        <Link to="/en/privacy" className="hover:text-black transition-colors">Privacy Policy</Link>
        <Link to="/en/terms" className="hover:text-black transition-colors">Terms of Service</Link>
        <Link to="/en/sla" className="hover:text-black transition-colors">SLA</Link>
        <Link to="/contato" className="hover:text-black transition-colors">Contact</Link>
        <Link to="/pt" className="hover:text-black transition-colors">Português</Link>
      </div>
    </footer>
  </div>
);

export default EnGovernanceLandingPage;
